import {
  Checkbox,
  Divider,
  Group,
  Input,
  Modal,
  Stack,
  Text,
} from "@mantine/core";
import { DatePickerInput as MantineDatePickerInput } from "@mantine/dates";
import { useEffect, useMemo } from "react";
import { Controller, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import MultiSelect from "src/components/shared/_FormInputs/MultiSelect";
import Select from "src/components/shared/_FormInputs/Select";
import { Button } from "src/components/shared/Button/Button";
import { useSummaryFilters } from "src/hooks/useSummaryFilters";
import { Flex } from "src/styled";
import { ENTRY_STATUS, entryStatusMap } from "src/utils/entryStatus";

interface ILogbookOption {
  label?: string;
  value: string;
}

const sortByOptions = [
  { label: "Entry Date", value: "date" },
  { label: "Open Date", value: "open" },
];

const dateTypeOptions = [
  { label: "Entry Date", value: "entrydate" },
  { label: "Open Date", value: "opendate" },
];

const directionOptions = [
  { label: "Ascending", value: "asc" },
  { label: "Descending", value: "desc" },
];

export interface AdvancedFiltersValues {
  logbook?: string[];
  status?: string[];
  dateFrom?: string;
  dateTo?: string;
  dateType: string;
  ref?: string;
  hasSnapshot: boolean;
  hasAttachments: boolean;
  hasChecklists: boolean;
  sortBy?: string;
  direction: "asc" | "desc";
}

export const buildSummarySearchParams = (
  vals: AdvancedFiltersValues,
  currentSearchParams?: URLSearchParams,
) => {
  const params = new URLSearchParams(currentSearchParams ?? "");

  if (vals.sortBy) {
    params.set("sort", vals.sortBy);
    params.set("dir", vals.direction);
  } else {
    params.delete("sort");
    params.delete("dir");
  }

  if (vals.dateFrom) params.set("dateFrom", vals.dateFrom);
  else params.delete("dateFrom");

  if (vals.dateTo) params.set("dateTo", vals.dateTo);
  else params.delete("dateTo");

  if (vals.dateType && vals.dateType !== "entrydate")
    params.set("dateType", vals.dateType);
  else params.delete("dateType");

  if (vals.ref) params.set("ref", vals.ref);
  else params.delete("ref");

  if (vals.logbook && vals.logbook.length > 0) {
    params.delete("logbook");
    vals.logbook.forEach(lb => params.append("logbook", lb));
  } else {
    params.delete("logbook");
  }

  if (vals.status && vals.status.length > 0) {
    params.delete("status");
    vals.status.forEach(st => params.append("status", st));
  } else {
    params.delete("status");
  }

  if (vals.hasSnapshot) params.set("hasSnapshot", "1");
  else params.delete("hasSnapshot");

  if (vals.hasAttachments) params.set("hasAttachments", "1");
  else params.delete("hasAttachments");

  if (vals.hasChecklists) params.set("hasChecklists", "1");
  else params.delete("hasChecklists");

  params.set("page", "1");

  return params;
};

export const buildSummaryRequestBody = (
  vals: AdvancedFiltersValues,
  entriesType: string,
) => ({
  params: {
    req: entriesType,
    ...(vals.sortBy
      ? {
          sort: vals.sortBy,
          dir: vals.direction,
        }
      : {}),
    ...(vals.dateFrom && {
      dateFrom: vals.dateFrom,
    }),
    ...(vals.dateTo && {
      dateTo: vals.dateTo,
    }),
    dateType: vals.dateType || "entrydate",
    ...(vals.hasSnapshot && { hasSnapshot: 1 }),
    ...(vals.hasAttachments && { hasAttachments: 1 }),
    ...(vals.hasChecklists && { hasChecklists: 1 }),
    ...(vals.ref && { ref: vals.ref }),
  },
  ...(vals.logbook &&
    vals.logbook.length > 0 && {
      _logbook: vals.logbook.length === 1 ? vals.logbook[0] : vals.logbook,
    }),
  ...(vals.status &&
    vals.status.length > 0 && {
      _status:
        vals.status.length === 1
          ? Number(vals.status[0])
          : vals.status.map(Number),
    }),
  _page: 1,
});

interface Props {
  opened: boolean;
  onClose: () => void;
  logbooks?: ILogbookOption[];
  replaceMaritimeText?: (t: string | undefined) => string | undefined;
  searchParams?: URLSearchParams;
  onApply?: (vals: AdvancedFiltersValues) => void;
  onGeneratePdf?: (vals: AdvancedFiltersValues) => void | Promise<void>;
  pdfLoading?: boolean;
}

const AdvancedFiltersModal = ({
  opened,
  onClose,
  logbooks = [],
  replaceMaritimeText = t => t,
  searchParams,
  onApply,
  onGeneratePdf,
  pdfLoading = false,
}: Props) => {
  const navigate = useNavigate();
  const filters = useSummaryFilters();
  const defaultValues = {
    logbook: undefined as string[] | undefined,
    status: undefined as string[] | undefined,
    dateFrom: undefined as string | undefined,
    dateTo: undefined as string | undefined,
    dateType: "entrydate",
    ref: undefined as string | undefined,
    hasSnapshot: false,
    hasAttachments: false,
    hasChecklists: false,
    sortBy: undefined as string | undefined,
    direction: "asc" as "asc" | "desc",
  };
  const { control, getValues, watch, reset } = useForm({
    defaultValues,
  });

  useEffect(() => {
    if (!opened) return;
    reset({
      logbook: filters.logbook.length > 0 ? filters.logbook : undefined,
      status:
        filters.status.length > 0
          ? filters.status.map(s => String(s))
          : undefined,
      dateFrom: filters.dateFrom,
      dateTo: filters.dateTo,
      dateType: filters.dateType,
      ref: filters.ref,
      hasSnapshot: filters.hasSnapshot,
      hasAttachments: filters.hasAttachments,
      hasChecklists: filters.hasChecklists,
      sortBy: filters.sort,
      direction: (filters.dir as "asc" | "desc") ?? "asc",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, reset]);

  const sortBy = watch("sortBy");

  const status = useMemo(
    () => [
      {
        label: entryStatusMap[ENTRY_STATUS.OPEN].label,
        value: String(ENTRY_STATUS.OPEN),
      },
      {
        label: replaceMaritimeText?.(
          entryStatusMap[ENTRY_STATUS.VERIFIED].label,
        ),
        value: String(ENTRY_STATUS.VERIFIED),
      },
      {
        label: entryStatusMap[ENTRY_STATUS.WITHDRAWN].label,
        value: String(ENTRY_STATUS.WITHDRAWN),
      },
    ],
    [replaceMaritimeText],
  );

  const handleClear = () => {
    reset(defaultValues);
    const params = new URLSearchParams(searchParams ?? "");
    [
      "dateFrom",
      "dateTo",
      "dateType",
      "ref",
      "logbook",
      "status",
      "hasSnapshot",
      "hasAttachments",
      "hasChecklists",
      "sort",
      "dir",
    ].forEach(k => params.delete(k));
    params.set("page", "1");
    navigate(`?${params.toString()}`, { replace: true });
    onClose();
  };

  const handleApply = () => {
    const vals = getValues();
    if (!vals) return;

    if (onApply) {
      onApply(vals);
      return;
    }

    const params = buildSummarySearchParams(vals, searchParams);

    if (params.toString() !== (searchParams?.toString() ?? "")) {
      navigate(`?${params.toString()}`, { replace: true });
    }
    onClose();
  };

  const handleGeneratePdf = () => {
    const vals = getValues();
    void onGeneratePdf?.(vals);
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      size="lg"
      title="Advanced Filters"
      keepMounted
      styles={{ body: { padding: 0 } }}>
      <Stack gap="sm" p="md" style={{ maxHeight: "65vh", overflowY: "auto" }}>
        <Stack gap={4}>
          <Text size="sm" c="text">
            Date Type
          </Text>
          <Select name="dateType" control={control} data={dateTypeOptions} />
        </Stack>

        <Flex gap={10}>
          <Stack gap={4} flex={1}>
            <Text size="sm" c="text">
              Date from
            </Text>
            <Controller
              name="dateFrom"
              control={control}
              render={({ field }) => (
                <MantineDatePickerInput
                  valueFormat="D MMM YYYY"
                  value={field.value ?? null}
                  onChange={value => field.onChange(value ?? undefined)}
                  placeholder="Pick date"
                  dropdownType="modal"
                  clearable
                />
              )}
            />
          </Stack>
          <Stack gap={4} flex={1}>
            <Text size="sm" c="text">
              Date to
            </Text>
            <Controller
              name="dateTo"
              control={control}
              render={({ field }) => (
                <MantineDatePickerInput
                  valueFormat="D MMM YYYY"
                  value={field.value ?? null}
                  onChange={value => field.onChange(value ?? undefined)}
                  placeholder="Pick date"
                  dropdownType="modal"
                  clearable
                />
              )}
            />
          </Stack>
        </Flex>

        <Stack gap={4}>
          <Text size="sm" c="text">
            Entry Ref. #
          </Text>
          <Controller
            name="ref"
            control={control}
            render={({ field }) => (
              <Input {...field} value={field.value ?? ""} />
            )}
          />
        </Stack>
        <Stack gap={4}>
          <Text size="sm" c="text">
            Logbook
          </Text>
          <MultiSelect
            name="logbook"
            control={control}
            data={logbooks}
            placeholder="Select logbook"
            searchable
            clearable
          />
        </Stack>
        <Stack gap={4}>
          <Text size="sm" c="text">
            Status
          </Text>
          <MultiSelect
            name="status"
            control={control}
            data={status}
            placeholder="Select status"
            clearable
          />
        </Stack>
        <Flex gap="sm">
          <Stack gap={4} flex={1}>
            <Text size="sm" c="text">
              Sort by
            </Text>
            <Select
              name="sortBy"
              control={control}
              data={sortByOptions}
              placeholder="Select sort field"
              clearable
            />
          </Stack>

          {sortBy && sortByOptions.some(s => s.value === sortBy) && (
            <Stack gap={4} flex={1}>
              <Text size="sm" c="text">
                Sort direction
              </Text>
              <Select
                name="direction"
                control={control}
                data={directionOptions}
                placeholder="Select direction"
              />
            </Stack>
          )}
        </Flex>

        <Text size="sm" c="text">
          Other filters
        </Text>
        <Stack gap="sm" pl="sm">
          <Controller
            name="hasSnapshot"
            control={control}
            render={({ field }) => (
              <Checkbox
                label="Has historical snapshot"
                checked={field.value}
                onChange={e => field.onChange(e.currentTarget.checked)}
              />
            )}
          />
          <Controller
            name="hasAttachments"
            control={control}
            render={({ field }) => (
              <Checkbox
                label="Has attachments"
                checked={field.value}
                onChange={e => field.onChange(e.currentTarget.checked)}
              />
            )}
          />
          <Controller
            name="hasChecklists"
            control={control}
            render={({ field }) => (
              <Checkbox
                label="Has checklists"
                checked={field.value}
                onChange={e => field.onChange(e.currentTarget.checked)}
              />
            )}
          />
        </Stack>
      </Stack>

      <Divider />
      <Group justify="space-between" p="md">
        <Button color="red" onClick={onClose}>
          Cancel
        </Button>
        <Group>
          <Button
            variant="outline"
            color="blue"
            onClick={handleGeneratePdf}
            loading={pdfLoading}
            disabled={!onGeneratePdf}>
            Generate PDF
          </Button>
          <Button variant="outline" color="gray" onClick={handleClear}>
            Clear Filters
          </Button>
          <Button onClick={handleApply}>Apply</Button>
        </Group>
      </Group>
    </Modal>
  );
};

export default AdvancedFiltersModal;
